import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, ChevronDown, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api, errMsg, fmtDate, today } from "@/lib/api";
import { PageHeader, StatCard, TableWrap, Empty, th, td, tr } from "@/components/common";

const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
const tone = (d) => (d == null ? "" : d >= 7 ? "text-rose-600 font-bold" : d >= 3 ? "text-amber-600 font-semibold" : "text-emerald-700");

function csv(d) {
  const lines = [["Vehicle", "From LR", "Loading Point", "Next LR", "Next Trip Start", "Idle Days"]];
  d.rows.forEach((r) => r.gaps.forEach((g) => lines.push([r.vehicle_number, g.from_lr, g.loading_point, g.to_lr, g.next_start, g.days])));
  lines.push([], ["Vehicle", "Idle Days (month)", "Avg", "Max", "Idle Since Last Loading Point"]);
  d.rows.forEach((r) => lines.push([r.vehicle_number, r.idle_days, r.avg_days, r.max_days, r.current_idle ?? ""]));
  const blob = new Blob(["\ufeff" + lines.map((x) => x.map(esc).join(",")).join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob); a.download = `Idle_Days_${d.month}.csv`; a.click();
}

export default function IdleDays() {
  const [month, setMonth] = useState(today().slice(0, 7));
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/reports/idle-days", { params: { month } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [month]);
  const standing = d ? d.rows.filter((r) => r.current_idle != null && r.current_idle >= 3).length : 0;
  return (
    <div className="space-y-6">
      <PageHeader title="Idle Days Report" subtitle="Loading Point ke baad agli trip shuru hone tak gaadi kitne din khadi rahi" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Input type="month" className="w-44" data-testid="idle-month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
        <Button variant="outline" size="sm" disabled={!d} data-testid="idle-csv-btn" onClick={() => csv(d)}><Download className="mr-1 h-4 w-4" />Download Excel (CSV)</Button>
      </div>
      {!d ? <div className="text-sm text-muted-foreground">Loading…</div> : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard testid="idle-total" label="Total Idle Days (is mahine)" value={d.total_idle} />
            <StatCard testid="idle-vehicles" label="Own Vehicles" value={d.rows.length} delay={60} />
            <StatCard testid="idle-standing" label="Abhi 3+ din se khadi" value={standing} accent={standing ? "text-rose-600" : ""} delay={120} />
          </div>
          {!d.rows.length ? <Empty testid="idle-empty" text="Koi apni gaadi nahi" /> : (
            <TableWrap testid="idle-table">
              <table className="w-full text-sm">
                <thead><tr>{["Vehicle", "Gaps", "Idle Days", "Avg / Gap", "Max", "Last Loading Point", "Abhi Khadi (din)"].map((h, i) => <th key={h} className={`${th} ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
                <tbody>
                  {d.rows.map((r) => (
                    <Fragment key={r.vehicle_id}>
                      <tr className={`${tr} cursor-pointer`} data-testid={`idle-row-${r.vehicle_id}`} onClick={() => setOpen(open === r.vehicle_id ? null : r.vehicle_id)}>
                        <td className={`${td} font-semibold`}><span className="inline-flex items-center gap-1">{open === r.vehicle_id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{r.vehicle_number}</span></td>
                        <td className={`${td} text-right`}>{r.gaps.length}</td>
                        <td className={`${td} amount text-right font-semibold`} data-testid={`idle-days-${r.vehicle_id}`}>{r.idle_days}</td>
                        <td className={`${td} amount text-right`}>{r.avg_days}</td>
                        <td className={`${td} amount text-right ${tone(r.max_days)}`}>{r.max_days}</td>
                        <td className={`${td} text-right text-muted-foreground`}>{r.last_loading_point ? `${fmtDate(r.last_loading_point)} · ${r.last_lr}` : "—"}</td>
                        <td className={`${td} amount text-right ${tone(r.current_idle)}`} data-testid={`idle-current-${r.vehicle_id}`}>{r.current_idle ?? "On trip"}</td>
                      </tr>
                      {open === r.vehicle_id && (
                        <tr><td colSpan={7} className="bg-muted/40 px-4 py-3">
                          {!r.gaps.length ? <span className="text-xs text-muted-foreground">Is mahine koi agli trip shuru nahi hui</span> : r.gaps.map((g, i) => (
                            <div key={i} className="flex justify-between py-0.5 text-xs" data-testid={`idle-gap-${r.vehicle_id}-${i}`}>
                              <span>{g.from_lr} Loading Point {fmtDate(g.loading_point)} → {g.to_lr} start {fmtDate(g.next_start)}</span>
                              <b className={tone(g.days)}>{g.days} din</b>
                            </div>
                          ))}
                        </td></tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          )}
        </>
      )}
    </div>
  );
}
